import { useCallback } from 'react';
import { create, type StateCreator } from 'zustand';
import { devtools } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';
import { AxiosError, HttpStatusCode, isAxiosError } from 'axios';
import { API_ROUTES } from '@/api/apiRoutes';
import { safeAxios } from '@/services/safeAxios';
import { wafChallengeManager } from '@/services/waf/WAFChallengeManager';
import { log } from '@/logger/logger';
import { type Edge, type Node } from '@/types/node';
import { sanitizeRecipe } from '@/components/Nodes/Utils';
import { track } from '@/utils/analytics';
import { posthog } from '@/providers/posthog';
import { applyFeatureFlagOverride } from '@/hooks/useFeatureFlag';
import { FF_BLOCK_EDITING_ON_FAILING_SAVES, FF_WEAVE_SIDE_BY_SIDE } from '@/consts/featureFlags';
import { postSetCoverPhotoToFigmaParent } from '@/utils/figma-embed-messages.utils';
import { TrackTypeEnum } from '@/hooks/useAnalytics';
import { RecipeVisibilityType } from '@/enums/recipe-visibility-type';
import { renameRecipe } from '@/api/recipes';
import { useIteratorRunConfigStore } from '@/state/iterator-run-config.state';
import { useBatchesStore } from '@/components/Recipe/RunFlow/batches.store';
import { getBatchStatus, isBatchDone } from '@/components/Recipe/RunFlow/batch.utils';
import type { Recipe } from '@/types/api/recipe';
import type { ImageRef, ResolvedAssets } from '@/types/api/assets';
import type { NodeDefinition } from '@/types/api/nodeDefinition';
import type { ModelPrice, ModelPricing } from '@/types/api/modelPrice';
import type { InvalidConnectionOptions } from '@/components/Recipe/Views/FlowView';
import type { User } from '@/types/auth.types';
import type { ModelApprovalStatus } from '@/types/api/modelApproval';
import type { ToolMetadata } from '@/types/design-app.types';

const logger = log.getLogger('WorkflowStore');

/**
 * Consecutive failed saves before editing is blocked with `'SaveFailing'`. A failure without a
 * response has already been retried 3 times by `axios-retry`; one with an HTTP status has not.
 */
export const SAVE_FAILURE_THRESHOLD = 3;

/**
 * Save-health telemetry: a Datadog log and a PostHog event. Sent whether or not the block is on, so it also
 * shows how often the block would fire where it is off; at most one of each per run of failures.
 */
const reportSaveHealth = (event: string, message: string, context: Record<string, unknown>) => {
  logger.warn(message, context);
  track(event, context, TrackTypeEnum.Product);
};

/**
 * How long a save request waits for the server's reply before failing, above the save route's p99. Axios times only
 * the request itself, not the waits around it (a security check, a retry), so a save can still outlast it.
 *
 * The time includes uploading the body, so a large workflow on a slow connection can time out while it is still
 * uploading. If too many saves time out, time out on no progress instead: no upload progress for a while
 * (`onUploadProgress`), or no reply once the body is fully sent.
 */
export const SAVE_REQUEST_TIMEOUT_MS = 30_000;

/** A save still unsettled this long after it started is reported as stuck. Past the request timeout, it waits elsewhere. */
export const SAVE_STUCK_REPORT_MS = 60_000;

/**
 * Reports a save still unsettled {@link SAVE_STUCK_REPORT_MS} after it started (`workflow_save_stuck`), with what its
 * request got through and whether a security check was running, and again once it settles. `requestConfig` goes on
 * the save request so the reports can tell whether its body went out and a reply began.
 */
const watchSave = (workflowId: string, saveFlowId: string | undefined, timeoutMs: number | undefined) => {
  const startedAt = Date.now();
  let bodySent = false;
  let replyStarted = false;
  let isStuck = false;
  let isSettled = false;
  const timer = setTimeout(() => {
    isStuck = true;
    reportSaveHealth('workflow_save_stuck', 'Workflow save still unsettled after a minute', {
      workflowId,
      saveFlowId,
      pendingMs: Date.now() - startedAt,
      timeoutMs: timeoutMs ?? null,
      bodySent,
      replyStarted,
      securityCheckActive: wafChallengeManager.hasActiveClearance(),
      securityCheckSlow: wafChallengeManager.isActiveClearanceSlow(),
      online: navigator.onLine,
      visibility: document.visibilityState,
    });
  }, SAVE_STUCK_REPORT_MS);
  return {
    requestConfig: {
      onUploadProgress: () => {
        bodySent = true;
      },
      onDownloadProgress: () => {
        replyStarted = true;
      },
    },
    settled: (outcome: 'saved' | 'failed', statusCode?: number) => {
      if (isSettled) return;
      isSettled = true;
      clearTimeout(timer);
      if (!isStuck) return;
      reportSaveHealth('workflow_save_stuck_settled', 'A stuck workflow save settled', {
        workflowId,
        saveFlowId,
        pendingMs: Date.now() - startedAt,
        outcome,
        statusCode,
      });
    },
  };
};

export type WorkflowRole = 'guest' | 'editor';

export type WorkflowRecipe = Pick<
  Recipe,
  | 'designAppMetadata'
  | 'toolConfig'
  | 'folderId'
  | 'scope'
  | 'id'
  | 'latestPublishedVersion'
  | 'name'
  | 'poster'
  | 'userPoster'
  | 'publishedVersions'
  | 'sections'
  | 'updatedAt'
  | 'graphUpdatedAt'
  | 'v3'
  | 'version'
  | 'visibility'
  | 'visibilityIncludesToolAudience'
  | 'canChangeToolAudience'
  | 'commentsVisible'
  | 'liveDraftVersion'
  | 'isDeleted'
  | 'workspaceId'
  | 'owner'
>;

/** Where a `graphUpdatedAt` the store adopts came from, for the adoption log. */
export type GraphUpdatedAtSource = 'batch_socket' | 'batch_poll' | 'batch_catch_up' | 'batch_request' | 'graph_changed';

export type PosterSource = 'auto' | 'user';

function parsePrices(modelPrices: ModelPricing): ModelPricesMaps {
  const modelPricesMaps = {
    modelsByType: new Map<string, ModelPrice[]>(),
    modelsByName: new Map<string, ModelPrice[]>(),
    defaultPrice: modelPrices.defaultPrice,
  };

  for (const model of modelPrices.prices) {
    // Index by modelType
    if (model.modelType) {
      const currentModelsByType: ModelPrice[] = modelPricesMaps.modelsByType.get(model.modelType) || [];
      currentModelsByType.push(model);
      modelPricesMaps.modelsByType.set(model.modelType, currentModelsByType);
    }

    // Index by modelName (for Replicate models), keyed lowercased so price lookups are
    // case-insensitive (uppercase variants like `Bytedance/seedance-2.0` still resolve).
    if (model.modelName) {
      const key = model.modelName.toLowerCase();
      const currentModelsByName: ModelPrice[] = modelPricesMaps.modelsByName.get(key) || [];
      currentModelsByName.push(model);
      modelPricesMaps.modelsByName.set(key, currentModelsByName);
    }
  }
  return modelPricesMaps;
}

type WorkflowLoadingKeys = 'recipe-version' | 'node-types' | 'wasm' | 'flow-graph' | 'model-prices' | 'workspace-fonts';
const WORKFLOW_LOADING_KEYS: WorkflowLoadingKeys[] = [
  'recipe-version',
  'node-types',
  'wasm',
  'flow-graph',
  'model-prices',
  'workspace-fonts',
];

// --- Data Slice ---
export interface ModelPricesMaps {
  modelsByType: Map<string, ModelPrice[]>;
  modelsByName: Map<string, ModelPrice[]>;
  defaultPrice: number;
}

type LoadingState = 'initial' | 'loading' | 'loaded' | 'error';

interface WorkflowDataState {
  /** Timestamp (ms) when current save started; null when not saving. Used to guard concurrent saves and log duration. */
  _saveStartedAt: number | null;
  /** Counts agent `graph_changed` deltas applied to the canvas, so a save can tell one landed while it ran. */
  agentEventsApplied: number;
  isLatestVersion: boolean;
  loadingStates: Map<WorkflowLoadingKeys, LoadingState>;
  modelPricesMaps: ModelPricesMaps;
  nodeTypes: NodeDefinition[];
  publicNodeTypes: NodeDefinition[];
  userNodeTypes: NodeDefinition[];
  recipe: WorkflowRecipe;
  resolvedAssets: ResolvedAssets; // Blobstore signed URLs for the current workflow, keyed by asset id
  workflowRole: WorkflowRole;
  /**
   * Mirrors the `weave_restricted` JWT claim (Figma embed mode only) — true when the
   * user is community-blocked or sharing-restricted. Presentation only: `workflowRole`
   * is already downgraded by the server-side `permission` ceiling, so this must never be
   * used to gate an operation, only to select a restricted-user UI treatment.
   */
  isWeaveRestricted: boolean;
}

export interface SaveRecipeOptions extends Pick<Partial<Recipe>, 'poster' | 'designAppMetadata'> {
  /** Reads the canvas as it is now: once when the save starts, and again for a follow-up save. */
  getGraph: () => { nodes: Node[]; edges: Edge[] };
  saveFlowId?: string;
}

interface WorkflowDataActions {
  addNodeType: (nodeType: NodeDefinition) => void;
  deleteSharedUser: (email: string) => Promise<void>;
  loadNodeTypes: () => Promise<void>;
  loadUserNodeTypes: (shouldUpdateNodeTypes?: boolean) => Promise<void>;
  loadPublicNodeTypes: (shouldUpdateNodeTypes?: boolean) => Promise<void>;
  loadPrices: () => Promise<void>;
  mergeResolvedAssets: (next: ResolvedAssets) => void;
  publishDesignApp: (toolMetadata?: ToolMetadata, workflowId?: string) => Promise<Recipe>;
  renameRecipe: (newName: string) => Promise<void>;
  resetWorkflowState: () => void;
  saveRecipe: (options: SaveRecipeOptions) => Promise<void>;
  setIsLatestVersion: (isLatestVersion: boolean) => void;
  setLoadingState: (key: WorkflowLoadingKeys, value: LoadingState) => void;
  setRecipe: (recipe: WorkflowRecipe) => void;
  /** Moves the workflow's `updatedAt` forward only, so an older timestamp never triggers a save Conflict. */
  adoptUpdatedAt: (updatedAt: string) => void;
  /**
   * Moves the `graphUpdatedAt` a save sends (as `lastGraphUpdatedAt`) to one the server sent with writes the canvas
   * is taking in (results, an agent's edit), forward only, so the save that follows them is not rejected as a
   * Conflict.
   */
  adoptGraphUpdatedAt: (workflowId: string, graphUpdatedAt: string, source: GraphUpdatedAtSource) => void;
  noteAgentEventApplied: () => void;
  setRecipePoster: (poster: ImageRef, source?: PosterSource) => Promise<void>;
  setWorkflowRole: (role: WorkflowRole) => void;
  setIsWeaveRestricted: (isWeaveRestricted: boolean) => void;
  shareRecipe: (emails: string[]) => Promise<{ users: User[] }>;
  updateRecipeVisibility: (newVisibility: RecipeVisibilityType) => Promise<void>;
}

export type WorkflowDataSlice = WorkflowDataState & WorkflowDataActions;

/** Side-by-side gate for store code; mirrors `useFeatureFlag`, including the `?featureFlagsOn=` override. Off before PostHog loads. */
const isSideBySideEnabled = (): boolean =>
  applyFeatureFlagOverride(
    FF_WEAVE_SIDE_BY_SIDE,
    posthog?.isFeatureEnabled(FF_WEAVE_SIDE_BY_SIDE),
    typeof window === 'undefined' ? '' : window.location.search,
  ) === true;

/** A flag gate for store code; mirrors `useFeatureFlag`, including the `?featureFlagsOn=` override. Off before PostHog loads. */
const isFlagEnabled = (flag: string): boolean =>
  applyFeatureFlagOverride(
    flag,
    posthog?.isFeatureEnabled(flag),
    typeof window === 'undefined' ? '' : window.location.search,
  ) === true;

const isBlockEditingOnFailingSavesEnabled = (): boolean => isFlagEnabled(FF_BLOCK_EDITING_ON_FAILING_SAVES);

/** The `updatedAt` to store after a write: forward-only with side-by-side on, otherwise the incoming value as before. */
const nextUpdatedAt = (current: string | undefined, incoming: string): string => {
  if (!isSideBySideEnabled() || !current) return incoming;
  const incomingMs = Date.parse(incoming);
  return Number.isNaN(incomingMs) || Date.parse(current) >= incomingMs ? current : incoming;
};

const isTime = (value: string | null | undefined): value is string => !!value && !Number.isNaN(Date.parse(value));

/**
 * What a save sends as `lastGraphUpdatedAt`: none until the server has given a valid `graphUpdatedAt`, since it
 * rejects one that is not a date. Without it the server checks `lastUpdatedAt` as before.
 */
const graphToken = (workflow: WorkflowRecipe): string | undefined =>
  isTime(workflow.graphUpdatedAt) ? workflow.graphUpdatedAt : undefined;

/** `incoming` when it is a valid time later than `current` (or `current` has none), otherwise `current`. */
const laterGraphUpdatedAt = (current: string | null | undefined, incoming: string): string | null | undefined =>
  isTime(incoming) && !(isTime(current) && Date.parse(current) >= Date.parse(incoming)) ? incoming : current;

/** How long a rejected save waits for a batch status: the 10s batch poll, plus the status request. */
const RESULT_STATUS_WAIT_MS = 15_000;

/** How a rejected save's wait for a batch status ended, for the failed-save log. */
type ResultStatusWait = 'arrived' | 'no_batch' | 'timed_out' | 'left';

// Initial state factory functions
const createInitialWorkflowDataState = (): WorkflowDataState => ({
  _saveStartedAt: null,
  agentEventsApplied: 0,
  isLatestVersion: true,
  loadingStates: new Map(WORKFLOW_LOADING_KEYS.map((key) => [key, 'initial'])),
  modelPricesMaps: {} as ModelPricesMaps,
  nodeTypes: [],
  publicNodeTypes: [],
  userNodeTypes: [],
  recipe: {} as WorkflowRecipe,
  resolvedAssets: {},
  workflowRole: 'guest',
  isWeaveRestricted: false,
});

const createInitialWorkflowUiState = (): WorkflowUiState => ({
  isGalleryParamsOpen: false,
  reactFlowContainerRect: undefined,
  workflowError: undefined,
  highlightedNodeIds: [],
  transientNodeIds: [],
  isInitialViewportClaimed: false,
  suppressedSelectionNode: null,
  isEditingNameOnWorkflow: false,
  isEditingNameOnToolbar: false,
  saveGap: null,
  isSaveHeld: false,
  heldSaveCount: 0,
});

const createInitialWorkflowEdgesState = (): WorkflowEdgesState => ({
  invalidConnection: null,
});

const createInitialWorkflowNodeValidationState = (): WorkflowNodeValidationState => ({
  nodeValidation: {},
});

const createWorkflowDataSlice: StateCreator<WorkflowState, [], [], WorkflowDataSlice> = (set, get, api) => {
  /**
   * The server writes a model result into the workflow before its batch status reaches this tab, so a save sent
   * in between carries the graph time from before the result. When that time is older than the result by more
   * than the conflict window, the server rejects the save, though this tab is about to apply that same result.
   * Rather than locking the canvas, the rejected save waits for the status and is sent again with the graph time
   * the status brings, which also includes the result the status just applied.
   *
   * For example, with a 60s window:
   * - 10:00:00 the last save, so the tab's graph time is 10:00:00.
   * - 10:05:58 an edit starts the 2s autosave timer.
   * - 10:06:00.0 the server writes a model result, moving the workflow's graph time to 10:06:00.
   * - 10:06:00.1 the autosave sends 10:00:00 and is rejected: six minutes is past the window.
   * - 10:06:00.2 the batch status moves the tab to 10:06:00 and applies the result, and the save is sent again.
   *
   * Resolves `arrived` once the tab's graph time is past `sent`, at once if a status landed while the save was in
   * flight. A tab with no batch still running has no status coming, so it does not wait. A batch whose run already
   * failed still counts while a node in it runs, since that node's result is written and reported all the same.
   */
  const waitForResultStatus = (workflowId: string, sent: string): Promise<ResultStatusWait> => {
    const settled = (): ResultStatusWait | undefined => {
      const { recipe: workflow } = get();
      if (workflow?.id !== workflowId) return 'left';
      if (isTime(workflow.graphUpdatedAt) && Date.parse(workflow.graphUpdatedAt) > Date.parse(sent)) return 'arrived';
      return undefined;
    };
    const hasUnfinishedBatch = Object.values(useBatchesStore.getState().batches).some(
      (batch) => batch.recipeId === workflowId && (!isBatchDone(batch) || getBatchStatus(batch) === 'RUNNING'),
    );
    const now = settled() ?? (hasUnfinishedBatch ? undefined : 'no_batch');
    if (now) return Promise.resolve(now);
    return new Promise((resolve) => {
      const finish = (outcome: ResultStatusWait) => {
        clearTimeout(timeout);
        unsubscribe();
        resolve(outcome);
      };
      const timeout = setTimeout(() => finish('timed_out'), RESULT_STATUS_WAIT_MS);
      const unsubscribe = api.subscribe(() => {
        const outcome = settled();
        if (outcome) finish(outcome);
      });
    });
  };

  return {
    // State
    ...createInitialWorkflowDataState(),
    // Actions
    addNodeType: (nodeType: NodeDefinition) => set((state) => ({ nodeTypes: [...state.nodeTypes, nodeType] })),
    deleteSharedUser: async (email: string) => {
      const response = await safeAxios.delete<{ recipeUpdatedAt: string }>(
        API_ROUTES.recipes.user({ recipeId: get().recipe.id, email }),
      );
      set((state) => ({
        recipe: {
          ...state.recipe,
          updatedAt: nextUpdatedAt(state.recipe.updatedAt, response.data.recipeUpdatedAt || new Date().toISOString()),
        },
      }));
    },
    loadNodeTypes: async () => {
      if (get().loadingStates.get('node-types') === 'loaded') {
        return;
      }
      get().setLoadingState('node-types', 'loading');
      await Promise.all([get().loadPublicNodeTypes(false), get().loadUserNodeTypes(false)]);
      if (get().loadingStates.get('node-types') !== 'error') {
        set({ nodeTypes: [...get().publicNodeTypes, ...get().userNodeTypes] });
        get().setLoadingState('node-types', 'loaded');
      }
    },
    loadUserNodeTypes: async (shouldUpdateNodeTypes: boolean = true) => {
      try {
        // Sent unconditionally so dragging a model from the old panel still works for users whose
        // models were already moved to the new saved-models table.
        const userNodeResponse = await safeAxios.get<NodeDefinition[]>(API_ROUTES.nodeDefinitions.user(), {
          params: { includeMigrated: true },
        });
        const userNodeTypes = userNodeResponse.data;
        if (shouldUpdateNodeTypes) {
          set({ nodeTypes: [...get().publicNodeTypes, ...userNodeTypes], userNodeTypes });
        } else {
          set({ userNodeTypes });
        }
      } catch (error) {
        logger.error('Error loading user node types', error);
        get().setLoadingState('node-types', 'error');
      }
    },
    loadPublicNodeTypes: async (shouldUpdateNodeTypes: boolean = true) => {
      try {
        const publicNodeTypesResponse = await safeAxios.get<NodeDefinition[]>(API_ROUTES.nodeDefinitions.public());
        const publicNodeTypes = publicNodeTypesResponse.data;
        if (shouldUpdateNodeTypes) {
          set({ nodeTypes: [...publicNodeTypes, ...get().userNodeTypes], publicNodeTypes });
        } else {
          set({ publicNodeTypes });
        }
      } catch (error) {
        logger.error('Error loading public node types', error);
        get().setLoadingState('node-types', 'error');
      }
    },
    loadPrices: async () => {
      if (get().loadingStates.get('model-prices') === 'loaded') {
        return;
      }
      get().setLoadingState('model-prices', 'loading');
      try {
        const modelPrices = await safeAxios.get<ModelPricing>(API_ROUTES.models.prices());
        set({ modelPricesMaps: parsePrices(modelPrices.data) });
        get().setLoadingState('model-prices', 'loaded');
      } catch (error) {
        logger.error('Error loading model prices', error);
        get().setLoadingState('model-prices', 'error');
      }
    },
    publishDesignApp: async (toolMetadata?: ToolMetadata, workflowId = get().recipe.id) => {
      // The metadata mints the tool row on the first deploy; a later one writes the fields it carries onto it.
      const response = await safeAxios.post<Recipe>(API_ROUTES.recipes.publish({ recipeId: workflowId }), toolMetadata);
      // Standalone preview can publish without loading this workflow into the editor store.
      if (get().recipe.id === workflowId) {
        set((state) => ({
          recipe: {
            ...state.recipe,
            latestPublishedVersion: response.data.latestPublishedVersion,
            publishedVersions: response.data.publishedVersions,
            version: response.data.latestVersion.version,
            designAppMetadata: response.data.designAppMetadata,
            toolConfig: response.data.toolConfig,
            updatedAt: nextUpdatedAt(state.recipe.updatedAt, response.data.updatedAt),
          },
        }));
      }
      return response.data;
    },
    renameRecipe: async (newName: string) => {
      const data = await renameRecipe(get().recipe.id, newName);
      set((state) => ({
        recipe: {
          ...state.recipe,
          name: newName,
          updatedAt: nextUpdatedAt(state.recipe.updatedAt, data.recipeUpdatedAt || new Date().toISOString()),
        },
      }));
    },
    saveRecipe: async (options: SaveRecipeOptions) => {
      // Suspend ALL saves (autosave + manual) while previewing a snapshot.
      if (get().previewSnapshotId !== null) {
        return;
      }

      const queryParams = new URLSearchParams(window.location.search);
      const debugDisableSave = queryParams.get('debugDisableSave');
      if (debugDisableSave === 'true') {
        // can be used for debugging purposes
        logger.warn('saveRecipe - blocked: debugDisableSave flag is true');
        return;
      }

      const { workflowRole, workflowError, recipe: currentRecipe, isSaveHeld } = get();
      if (workflowRole !== 'editor' || isEditingBlocked(workflowError)) {
        return;
      }
      if (isSaveHeld) {
        set((state) => ({ heldSaveCount: state.heldSaveCount + 1 }));
        return;
      }

      const graph = options.getGraph();
      // Exclude transiently-staged nodes (template hover-preview) so they're never persisted (WEA-5004).
      const { transientNodeIds } = get();
      const persistableNodes = transientNodeIds.length
        ? graph.nodes.filter((node) => !transientNodeIds.includes(node.id))
        : graph.nodes;

      if (persistableNodes.length === 0) {
        logger.info('Save blocked: empty nodes array', {
          saveFlowId: options.saveFlowId,
          workflowRole,
          edgesCount: graph.edges.length,
        });
        return;
      }

      const saveStartedAt = get()._saveStartedAt;
      if (saveStartedAt != null) {
        const durationMs = Date.now() - saveStartedAt;
        logger.warn('Save skipped: save already in progress', {
          saveFlowId: options.saveFlowId,
          saveStartedAt,
          durationMs,
        });
        return;
      }

      const { nodes, edges } = sanitizeRecipe(persistableNodes, graph.edges);

      const designAppMetadata = options.designAppMetadata || undefined;

      const dataToSave = {
        nodes,
        edges,
        v3: currentRecipe.v3,
        posterImageUrl: options.poster || undefined,
        designAppMetadata,
        lastUpdatedAt: currentRecipe.updatedAt || new Date().toISOString(),
        lastGraphUpdatedAt: graphToken(currentRecipe),
      };

      // A save sends the canvas as it was when it started, so one that overlapped an applied agent event is saved again.
      const sideBySide = isSideBySideEnabled();
      const agentEventsAtStart = get().agentEventsApplied;
      const shouldSaveAgain = () =>
        sideBySide &&
        get().recipe?.id === currentRecipe.id &&
        !isEditingBlocked(get().workflowError) &&
        get().agentEventsApplied > agentEventsAtStart;
      let saveAgain = false;
      let resultWaitMs: number | undefined;
      const timeoutMs = isBlockEditingOnFailingSavesEnabled() ? SAVE_REQUEST_TIMEOUT_MS : undefined;
      const watch = watchSave(currentRecipe.id, options.saveFlowId, timeoutMs);
      try {
        set({ _saveStartedAt: Date.now() });
        const response = await safeAxios.post<Recipe>(
          API_ROUTES.recipes.save({ recipeId: currentRecipe.id }),
          dataToSave,
          {
            ...watch.requestConfig,
            ...(timeoutMs !== undefined && { timeout: timeoutMs }),
          },
        );
        watch.settled('saved');

        saveAgain = shouldSaveAgain();
        const savedGraphUpdatedAt = response.data.graphUpdatedAt;
        const endedGap = get().recipe?.id === currentRecipe.id ? get().saveGap : null;
        // A response that lands after navigating to another workflow belongs to the one left behind.
        set((state) =>
          state.recipe?.id !== currentRecipe.id
            ? state
            : {
                recipe: {
                  ...state.recipe,
                  updatedAt: nextUpdatedAt(state.recipe.updatedAt, response.data.updatedAt || new Date().toISOString()),
                  // A server that doesn't send it isn't checking it, so stop sending the old one.
                  graphUpdatedAt: savedGraphUpdatedAt
                    ? laterGraphUpdatedAt(state.recipe.graphUpdatedAt, savedGraphUpdatedAt)
                    : undefined,
                },
                saveGap: null,
                // Nothing else clears 'Unknown' — it is set by any failed save and renders nothing, so
                // without this it persists for the rest of the session once a single save has failed.
                workflowError:
                  state.workflowError === 'Unknown' && isBlockEditingOnFailingSavesEnabled()
                    ? undefined
                    : state.workflowError,
              },
        );
        // Reported even below the threshold, to show how often a gap recovers on its own.
        if (endedGap) {
          reportSaveHealth('workflow_save_failure_recovered', 'Workflow saves recovered after a run of failures', {
            workflowId: currentRecipe.id,
            gapId: endedGap.id,
            gapDurationMs: Date.now() - endedGap.startedAt,
            failureCount: endedGap.failures,
            reachedThreshold: endedGap.failures >= SAVE_FAILURE_THRESHOLD,
          });
        }
      } catch (error) {
        let message = 'Unknown error';
        let responseMessage: string | undefined = undefined;
        let response: unknown = undefined;
        let statusCode: number | undefined = undefined;

        const axiosError = isAxiosError<{ message: string }>(error) ? error : undefined;
        if (axiosError) {
          message = axiosError.message;
          responseMessage = axiosError.response?.data?.message;
          response = axiosError.response?.data;
          statusCode = axiosError.response?.status;
        }
        watch.settled('failed', statusCode);
        const timedOut = axiosError?.code === AxiosError.ECONNABORTED && axiosError.message.includes('timeout');

        const isConflict = statusCode === HttpStatusCode.Conflict;
        const saveStartedAt = get()._saveStartedAt;
        const durationMs = saveStartedAt != null ? Date.now() - saveStartedAt : undefined;

        saveAgain = isConflict && shouldSaveAgain();
        // Only a save that sent a graph time can have been rejected for a result write.
        const waitStartedAt = Date.now();
        const resultStatus =
          isConflict && !saveAgain && dataToSave.lastGraphUpdatedAt
            ? await waitForResultStatus(currentRecipe.id, dataToSave.lastGraphUpdatedAt)
            : undefined;
        if (resultStatus) resultWaitMs = Date.now() - waitStartedAt;
        saveAgain ||= resultStatus === 'arrived';
        if (sideBySide && isConflict) {
          track('workflow_save.conflict', { willRetry: saveAgain });
        }

        logger.error(`Saving recipe failed`, error, {
          saveFlowId: options.saveFlowId,
          recipeId: currentRecipe.id,
          message,
          responseMessage,
          response,
          statusCode,
          timedOut,
          sentLastSavedAt: dataToSave.lastUpdatedAt,
          sentLastGraphUpdatedAt: dataToSave.lastGraphUpdatedAt,
          currentLastSavedAt: currentRecipe.updatedAt,
          nodesCount: nodes.length,
          edgesCount: edges.length,
          payloadSize: JSON.stringify(dataToSave).length,
          isAxiosError: !!axiosError,
          isConflict,
          saveDurationMs: durationMs,
          ...(sideBySide ? { willRetry: saveAgain } : {}),
          ...(resultStatus && { resultStatus, resultWaitMs }),
        });

        if (!saveAgain) {
          let workflowError: WorkflowError = 'Unknown';
          if (axiosError?.response?.status === HttpStatusCode.Conflict) {
            workflowError = 'Conflict';
          }

          const isBlockEnabled = isBlockEditingOnFailingSavesEnabled();
          let nextError: WorkflowError = workflowError;
          // A save that outlived its workflow must not feed or lock the one open now.
          const isCurrentWorkflow = resultStatus !== 'left' && get().recipe?.id === currentRecipe.id;

          // A conflict is terminal on its own and must not feed the gap: it is resolved by a refresh,
          // not by saves starting to work again.
          if (isCurrentWorkflow && workflowError !== 'Conflict') {
            const previous = get().saveGap;
            const gap: SaveGap = {
              id: previous?.id ?? crypto.randomUUID(),
              startedAt: previous?.startedAt ?? Date.now(),
              failures: (previous?.failures ?? 0) + 1,
              lastStatus: statusCode,
              hadNetworkError: (previous?.hadNetworkError ?? false) || statusCode === undefined,
            };
            set({ saveGap: gap });

            // Once per gap, on the crossing. A block already showing stays.
            if (gap.failures === SAVE_FAILURE_THRESHOLD && !isEditingBlocked(get().workflowError)) {
              reportSaveHealth('workflow_save_failure_detected', 'Workflow saves are not reaching the server', {
                workflowId: currentRecipe.id,
                gapId: gap.id,
                consecutiveFailures: gap.failures,
                lastStatus: gap.lastStatus,
                hadNetworkError: gap.hadNetworkError,
                enforced: isBlockEnabled,
              });
              if (isBlockEnabled) nextError = 'SaveFailing';
            }
          }
          // A save already in flight when a block engaged can still fail afterwards. Side-by-side
          // never replaces a block. Under block-editing-on-failing-saves only a conflict may: it is the
          // server's answer, while 'Unknown' is retryable and renders nothing, so letting it replace
          // the block would silently re-enable editing. With both off, the failure replaces it.
          const replacesBlock = !sideBySide && (!isBlockEnabled || workflowError === 'Conflict');
          if (isCurrentWorkflow && (!isEditingBlocked(get().workflowError) || replacesBlock)) {
            get().setWorkflowError(nextError);
          }
          track('save_error', { errorType: workflowError }, TrackTypeEnum.Product);

          throw error;
        }
      } finally {
        set({ _saveStartedAt: null });
      }
      if (saveAgain) {
        await get().saveRecipe(options);
        // A failed resend logs as another "Saving recipe failed" with the same saveFlowId and throws before this.
        if (resultWaitMs !== undefined) {
          logger.info('Save sent again after a batch status', {
            saveFlowId: options.saveFlowId,
            workflowId: currentRecipe.id,
            resultWaitMs,
          });
        }
      }
      // });
    },
    setIsLatestVersion: (isLatestVersion) => set({ isLatestVersion }),
    setLoadingState: (key: WorkflowLoadingKeys, value: LoadingState) => {
      set((state) => ({ loadingStates: new Map(state.loadingStates).set(key, value) }));
    },
    setRecipe: (recipe) => set({ recipe }),
    adoptUpdatedAt: (updatedAt) =>
      set((state) => {
        const next = nextUpdatedAt(state.recipe?.updatedAt, updatedAt);
        return next === state.recipe?.updatedAt ? state : { recipe: { ...state.recipe, updatedAt: next } };
      }),
    adoptGraphUpdatedAt: (workflowId, graphUpdatedAt, source) => {
      const { recipe: workflow } = get();
      if (workflow?.id !== workflowId) return;
      if (laterGraphUpdatedAt(workflow.graphUpdatedAt, graphUpdatedAt) === workflow.graphUpdatedAt) return;
      set({ recipe: { ...workflow, graphUpdatedAt } });
      // Debug, so it stays out of Datadog: it fires once per model result per open tab. The server's
      // recipe.save.conflict_check `token` tag shows whether saves carry the adopted graph time.
      logger.debug('graph_updated_at.adopted', {
        workflowId,
        source,
        from: workflow.graphUpdatedAt,
        to: graphUpdatedAt,
      });
    },
    noteAgentEventApplied: () => set((state) => ({ agentEventsApplied: state.agentEventsApplied + 1 })),
    // Keep completed variants when a stale response omits them. Unchanged polls are a no-op.
    mergeResolvedAssets: (next) =>
      set((state) => {
        let resolvedAssets = state.resolvedAssets;
        for (const [id, incoming] of Object.entries(next)) {
          const previous = resolvedAssets[id];
          const merged = {
            ...previous,
            ...incoming,
            createdAt: incoming.createdAt ?? previous?.createdAt,
            signedThumbnailUrl: incoming.signedThumbnailUrl ?? previous?.signedThumbnailUrl,
            signedWebpUrl: incoming.signedWebpUrl ?? previous?.signedWebpUrl,
          };
          // Optional fields remain absent when neither response supplied them.
          if (!merged.createdAt) delete merged.createdAt;
          if (!merged.signedThumbnailUrl) delete merged.signedThumbnailUrl;
          if (!merged.signedWebpUrl) delete merged.signedWebpUrl;
          if (
            !previous ||
            previous.type !== merged.type ||
            previous.createdAt !== merged.createdAt ||
            previous.signedUrl !== merged.signedUrl ||
            previous.signedThumbnailUrl !== merged.signedThumbnailUrl ||
            previous.signedWebpUrl !== merged.signedWebpUrl
          ) {
            if (resolvedAssets === state.resolvedAssets) resolvedAssets = { ...resolvedAssets };
            resolvedAssets[id] = merged;
          }
        }
        return resolvedAssets === state.resolvedAssets ? state : { resolvedAssets };
      }),
    setRecipePoster: async (poster, source = 'user') => {
      const response = await safeAxios.put<{ updatedAt: string; poster: string; userPoster: boolean }>(
        API_ROUTES.recipes.poster({ recipeId: get().recipe.id }),
        {
          url: poster.url,
          key: poster.key,
          source,
        },
      );
      set((state) => ({
        recipe: {
          ...state.recipe,
          updatedAt: nextUpdatedAt(state.recipe.updatedAt, response.data.updatedAt || new Date().toISOString()),
          poster: response.data.poster,
          userPoster: response.data.userPoster,
        },
      }));
      postSetCoverPhotoToFigmaParent(response.data.poster);
    },
    setWorkflowRole: (role) => set({ workflowRole: role }),
    setIsWeaveRestricted: (isWeaveRestricted) => set({ isWeaveRestricted }),
    shareRecipe: async (emails) => {
      track('share_workflow_modal_share_recipe', { emails }, TrackTypeEnum.BI);
      const response = await safeAxios.post<{ users: User[]; recipeUpdatedAt: string }>(
        API_ROUTES.recipes.share({ recipeId: get().recipe.id }),
        {
          emails,
        },
      );
      set((state) => ({
        recipe: {
          ...state.recipe,
          updatedAt: nextUpdatedAt(state.recipe.updatedAt, response.data.recipeUpdatedAt || new Date().toISOString()),
        },
      }));
      return { users: response.data.users };
    },
    updateRecipeVisibility: async (newVisibility) => {
      track('share_workflow_modal_visibility_selected', { visibility: newVisibility }, TrackTypeEnum.BI);
      try {
        const response = await safeAxios.put<{ recipeUpdatedAt: string }>(
          API_ROUTES.recipes.byId({ recipeId: get().recipe.id }),
          {
            visibility: newVisibility,
          },
        );
        set((state) => ({
          recipe: {
            ...state.recipe,
            visibility: newVisibility,
            updatedAt: nextUpdatedAt(state.recipe.updatedAt, response.data.recipeUpdatedAt || new Date().toISOString()),
          },
        }));
      } catch (e) {
        logger.error('Error updating recipe in updateRecipeVisibility.', e);
      }
    },
    resetWorkflowState: () => set(createInitialWorkflowDataState()),
  };
};

// --- UI Slice ---
export type WorkflowError = 'Conflict' | 'Unknown' | 'MultiTab' | 'SaveFailing' | 'Disconnected';
/** A `WorkflowError` that blocks editing until a refresh; see {@link isEditingBlocked}. */
export type BlockingWorkflowError = Exclude<WorkflowError, 'Unknown'>;

/**
 * Whether this client can no longer be trusted to edit — a stale version ('Conflict'), another live
 * tab holding the workflow ('MultiTab'), saves that keep failing ('SaveFailing'), or saves held too long
 * because a run's results couldn't be confirmed ('Disconnected'). Shared by the
 * save path, the role derivation and the canvas chrome so they cannot drift; 'Unknown' is a
 * retryable save error, not a block. All are resolved only by a refresh: a client that was cut off
 * is stale about anything the server did meanwhile.
 */
export function isEditingBlocked(workflowError: WorkflowError | undefined): workflowError is BlockingWorkflowError {
  return (
    workflowError === 'Conflict' ||
    workflowError === 'MultiTab' ||
    workflowError === 'SaveFailing' ||
    workflowError === 'Disconnected'
  );
}

interface WorkflowUiState {
  isGalleryParamsOpen: boolean;
  reactFlowContainerRect?: DOMRect;
  workflowError?: WorkflowError;
  highlightedNodeIds: string[];
  /** Node ids staged transiently (template hover-preview); excluded from save. In-memory only. */
  transientNodeIds: string[];
  /**
   * True while some controller owns the initial canvas viewport (e.g. an onboarding flow that zooms and
   * frames the canvas itself), so the canvas skips its one-time auto-fit-on-load. In-memory only.
   */
  isInitialViewportClaimed: boolean;
  suppressedSelectionNode: { nodeId: string; startX: number; startY: number } | null;
  isEditingNameOnWorkflow: boolean;
  isEditingNameOnToolbar: boolean;
  /**
   * The run of consecutive failed saves the client is currently in, or null when the last save
   * succeeded. Lives in the store so the save path can write it; the canvas resets it on mount.
   * Conflicts are excluded — they have their own terminal handling.
   */
  saveGap: SaveGap | null;
  /**
   * True while a run's results may be missing from this client — the socket dropped mid-run, or a
   * reload hasn't caught up yet — so a save could erase them. Saves are skipped, not failed.
   */
  isSaveHeld: boolean;
  /** Saves skipped by the current hold, reported with how it ended. */
  heldSaveCount: number;
}

/**
 * A run of consecutive failed saves. `id` ties every log emitted during the gap together, so a
 * single user's gap can be reconstructed from its failures through to its detection and recovery.
 */
export interface SaveGap {
  id: string;
  startedAt: number;
  failures: number;
  /** HTTP status of the most recent failure; absent when the request got no response at all. */
  lastStatus?: number;
  /** True once any failure in the gap came back without a response (offline, DNS, CORS). */
  hadNetworkError: boolean;
}

interface WorkflowUiActions {
  setIsGalleryParamsOpen: (isOpen: boolean) => void;
  setReactFlowContainerRect: (rect: DOMRect) => void;
  getReactFlowContainerRect: () => DOMRect | undefined;
  setWorkflowError: (error: WorkflowError | undefined) => void;
  setHighlightedNodeIds: (nodeIds: string[]) => void;
  clearHighlightedNodeIds: () => void;
  setTransientNodeIds: (nodeIds: string[]) => void;
  setInitialViewportClaimed: (claimed: boolean) => void;
  setSuppressedSelectionNode: (nodeId: string, startX: number, startY: number) => void;
  clearNodeIdToSuppressSelection: () => void;
  resetWorkflowUiState: () => void;
  setIsEditingNameOnWorkflow: (isEditingNameOnWorkflow: boolean) => void;
  setIsEditingNameOnToolbar: (isEditingNameOnToolbar: boolean) => void;
}

export type WorkflowUiSlice = WorkflowUiState & WorkflowUiActions;

const createWorkflowUiSlice: StateCreator<WorkflowState, [], [], WorkflowUiSlice> = (set, get) => ({
  ...createInitialWorkflowUiState(),
  setIsGalleryParamsOpen: (isOpen: boolean) => set(() => ({ isGalleryParamsOpen: isOpen })),
  setReactFlowContainerRect: (rect: DOMRect) => set(() => ({ reactFlowContainerRect: rect })),
  getReactFlowContainerRect: () => get().reactFlowContainerRect,
  setWorkflowError: (error) => set(() => ({ workflowError: error })),
  setHighlightedNodeIds: (nodeIds: string[]) => set(() => ({ highlightedNodeIds: nodeIds })),
  setTransientNodeIds: (nodeIds: string[]) => set(() => ({ transientNodeIds: nodeIds })),
  setInitialViewportClaimed: (claimed: boolean) => set(() => ({ isInitialViewportClaimed: claimed })),
  clearHighlightedNodeIds: () => set(() => ({ highlightedNodeIds: [] })),
  setSuppressedSelectionNode: (nodeId, startX, startY) =>
    set(() => ({ suppressedSelectionNode: { nodeId, startX, startY } })),
  clearNodeIdToSuppressSelection: () => set(() => ({ suppressedSelectionNode: null })),
  setIsEditingNameOnWorkflow: (isEditingNameOnWorkflow: boolean) => set(() => ({ isEditingNameOnWorkflow })),
  setIsEditingNameOnToolbar: (isEditingNameOnToolbar: boolean) => set(() => ({ isEditingNameOnToolbar })),
  resetWorkflowUiState: () => set(() => createInitialWorkflowUiState()),
});

// --- Edges Slice ---
interface WorkflowEdgesState {
  invalidConnection: InvalidConnectionOptions | null;
}

interface WorkflowEdgesActions {
  setInvalidConnection: (invalidConnection: InvalidConnectionOptions | null) => void;
  resetWorkflowEdgesState: () => void;
}

export type WorkflowEdgesSlice = WorkflowEdgesState & WorkflowEdgesActions;

const createWorkflowEdgesSlice: StateCreator<WorkflowState, [], [], WorkflowEdgesSlice> = (set) => ({
  ...createInitialWorkflowEdgesState(),
  setInvalidConnection: (invalidConnection) => set(() => ({ invalidConnection })),
  resetWorkflowEdgesState: () => set(() => createInitialWorkflowEdgesState()),
});

// --- Node Validation Slice ---

type MissingRequiredInputError = {
  type: 'missingRequiredInputs';
  extraInfo: {
    keys: string[];
  };
};

type EmptyRequiredInputError = {
  type: 'emptyRequiredInputs';
  extraInfo: {
    keys: string[];
  };
};

// Currently we only have one type of validation, but we can add more in the future

type NodeValidationError = MissingRequiredInputError | EmptyRequiredInputError;

interface WorkflowNodeValidationState {
  nodeValidation: Record<string, Record<NodeValidationError['type'], NodeValidationError | undefined>>;
}

interface WorkflowNodeValidationActions {
  setNodeValidation: (nodeId: string, validation: NodeValidationError) => void;
  removeNodeValidationByType: (nodeId: string, type: NodeValidationError['type']) => void;
  resetWorkflowNodeValidationState: () => void;
}

export type WorkflowNodeValidationSlice = WorkflowNodeValidationState & WorkflowNodeValidationActions;

const createWorkflowNodeValidationSlice: StateCreator<WorkflowState, [], [], WorkflowNodeValidationSlice> = (set) => ({
  ...createInitialWorkflowNodeValidationState(),
  setNodeValidation: (nodeId, validation) =>
    set((state) => ({
      nodeValidation: {
        ...state.nodeValidation,
        [nodeId]: { ...(state.nodeValidation[nodeId] || {}), [validation.type]: validation },
      },
    })),
  removeNodeValidationByType: (nodeId, type) =>
    set((state) => ({
      nodeValidation: {
        ...state.nodeValidation,
        [nodeId]: { ...(state.nodeValidation[nodeId] || {}), [type]: undefined },
      },
    })),
  resetWorkflowNodeValidationState: () => set(() => createInitialWorkflowNodeValidationState()),
});

// --- Version Preview Slice ---
interface WorkflowVersionPreviewState {
  isVersionHistoryDrawerOpen: boolean;
  /** Opaque snapshot identifier from the server; non-null iff a snapshot is being previewed. */
  previewSnapshotId: string | null;
  /** Bump to force Recipe.tsx to re-fetch the recipe (e.g., after restore). */
  recipeReloadCounter: number;
}

interface WorkflowVersionPreviewActions {
  /**
   * Open/close the drawer. Only editors can open it, so it simply flips the role to `guest` while
   * open (read-only browsing) and back to `editor` on close, clearing any active preview. The canvas
   * swap is driven imperatively by the drawer via `flowView.previewGraph` (a reversible off-undo edit).
   */
  setIsVersionHistoryDrawerOpen: (open: boolean) => void;
  /** Marks a snapshot as the previewed one (UI state only; the caller performs the canvas swap). */
  enterPreviewMode: (args: { snapshotId: string }) => void;
  exitPreviewMode: () => void;
  triggerRecipeReload: () => void;
  resetWorkflowVersionPreviewState: () => void;
}

export type WorkflowVersionPreviewSlice = WorkflowVersionPreviewState & WorkflowVersionPreviewActions;

const createInitialWorkflowVersionPreviewState = (): WorkflowVersionPreviewState => ({
  isVersionHistoryDrawerOpen: false,
  previewSnapshotId: null,
  recipeReloadCounter: 0,
});

const createWorkflowVersionPreviewSlice: StateCreator<WorkflowState, [], [], WorkflowVersionPreviewSlice> = (
  set,
  get,
) => ({
  ...createInitialWorkflowVersionPreviewState(),
  setIsVersionHistoryDrawerOpen: (open) => {
    if (open === get().isVersionHistoryDrawerOpen) return;
    // Only editors can open the drawer, so read-only browsing flips to guest while open; closing
    // clears any active preview and returns to editor (the canvas is restored by the drawer's
    // unmount cleanup via clearPreview).
    set(
      open
        ? { isVersionHistoryDrawerOpen: true, workflowRole: 'guest' }
        : { isVersionHistoryDrawerOpen: false, previewSnapshotId: null, workflowRole: 'editor' },
    );
  },
  enterPreviewMode: ({ snapshotId }) => {
    set({ previewSnapshotId: snapshotId });
  },
  exitPreviewMode: () => {
    if (get().previewSnapshotId === null) return;
    set({ previewSnapshotId: null });
  },
  triggerRecipeReload: () => set((state) => ({ recipeReloadCounter: state.recipeReloadCounter + 1 })),
  resetWorkflowVersionPreviewState: () => set(() => createInitialWorkflowVersionPreviewState()),
});

// --- Model Approval Slice ---
type WorkflowModelApprovalSlice = {
  modelApprovalStatuses: Record<string, ModelApprovalStatus>;
  setModelApprovalStatuses: (statuses: Record<string, ModelApprovalStatus>) => void;
  resetWorkflowModelApprovalState: () => void;
};

const createInitialWorkflowModelApprovalState = () => ({
  modelApprovalStatuses: {} as Record<string, ModelApprovalStatus>,
});

const createWorkflowModelApprovalSlice: StateCreator<WorkflowModelApprovalSlice> = (set) => ({
  ...createInitialWorkflowModelApprovalState(),
  setModelApprovalStatuses: (statuses) => set({ modelApprovalStatuses: statuses }),
  resetWorkflowModelApprovalState: () => set(createInitialWorkflowModelApprovalState()),
});

// --- Combined Store ---
export type WorkflowState = WorkflowDataSlice &
  WorkflowUiSlice &
  WorkflowEdgesSlice &
  WorkflowNodeValidationSlice &
  WorkflowVersionPreviewSlice &
  WorkflowModelApprovalSlice;

export const useWorkflowStore = create<WorkflowState>()(
  devtools(
    (...args) => ({
      ...createWorkflowDataSlice(...args),
      ...createWorkflowUiSlice(...args),
      ...createWorkflowEdgesSlice(...args),
      ...createWorkflowNodeValidationSlice(...args),
      ...createWorkflowVersionPreviewSlice(...args),
      ...createWorkflowModelApprovalSlice(...args),
    }),
    {
      name: 'Workflow Store',
      enabled: import.meta.env.DEV,
    },
  ),
);

export const useIsWorkflowLoading = () => {
  const isLoading = useWorkflowStore(
    useShallow((state) => Array.from(state.loadingStates.entries()).some(([_, value]) => value === 'loading')),
  );
  return isLoading;
};

export const useUserWorkflowRole = (): WorkflowRole => {
  const workflowRole = useWorkflowStore((state) => state.workflowRole);
  // Every case forces a read-only 'guest' role:
  // - 'Conflict': a 409 save means this client is on a stale (old) version, so
  //   edits can't pile up on a version the server keeps rejecting.
  // - 'MultiTab': the server detected another live tab editing this workflow for
  //   the same user, so this tab drops to read-only to avoid conflicting edits.
  // - 'SaveFailing': consecutive saves keep failing, so edits aren't reaching the server.
  // - 'Disconnected': saves were held past the hold's time window because a run's results couldn't be
  //   confirmed, so this client may be missing results its next save would erase.
  // All hold until the user refreshes.
  // Derived here rather than mutating the stored role because reactive writers —
  // Recipe.tsx's getRecipe effect and EmbedWorkflow's token effect — re-promote
  // owners to 'editor' and would otherwise clobber a stored downgrade while the
  // condition is still unresolved.
  const editingBlocked = useWorkflowStore((state) => isEditingBlocked(state.workflowError));
  return editingBlocked ? 'guest' : workflowRole;
};

export const useModelPricesMaps = () => {
  const modelPricesMaps = useWorkflowStore((state) => state.modelPricesMaps);
  return modelPricesMaps;
};

/** The response-scoped Blobstore URL map. Pair with `assetUrl` / `assetThumbnailUrl`. */
export const useResolvedAssets = (): ResolvedAssets => useWorkflowStore((state) => state.resolvedAssets);

export const useResetWorkflowState = () => {
  const resetWorkflowState = useWorkflowStore((state) => state.resetWorkflowState);
  const resetWorkflowUiState = useWorkflowStore((state) => state.resetWorkflowUiState);
  const resetWorkflowEdgesState = useWorkflowStore((state) => state.resetWorkflowEdgesState);
  const resetWorkflowNodeValidationState = useWorkflowStore((state) => state.resetWorkflowNodeValidationState);
  const resetWorkflowVersionPreviewState = useWorkflowStore((state) => state.resetWorkflowVersionPreviewState);
  const resetWorkflowModelApprovalState = useWorkflowStore((state) => state.resetWorkflowModelApprovalState);
  const clearWorkflowRunModes = useIteratorRunConfigStore((state) => state.clearWorkflowRunModes);

  return useCallback(() => {
    const workflowId = useWorkflowStore.getState().recipe.id;
    if (workflowId) {
      clearWorkflowRunModes(workflowId);
    }
    resetWorkflowState();
    resetWorkflowUiState();
    resetWorkflowEdgesState();
    resetWorkflowNodeValidationState();
    resetWorkflowVersionPreviewState();
    resetWorkflowModelApprovalState();
  }, [
    clearWorkflowRunModes,
    resetWorkflowState,
    resetWorkflowUiState,
    resetWorkflowEdgesState,
    resetWorkflowNodeValidationState,
    resetWorkflowVersionPreviewState,
    resetWorkflowModelApprovalState,
  ]);
};
